import { HttpStatusCode } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { PageTitle } from '@components/search-channel-group/search-channel-group.component';
import { customGraphSize, CustomGraphType } from '@models/custom-graph';
import { gaugeMaxDivisions, gaugeMinDivisions } from '@models/graphs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@service/auth.service';
import { ChannelService } from '@service/channel.service';
import { ConfirmationPromptService } from '@service/confirmation-prompt.service';
import { NotificationService } from '@service/notification.service';
import { AlarmSetting } from 'ops/common/models/alarm-setting';
import { Channel, ChannelFns } from 'ops/common/models/channel';
import {
    ObjectSchema,
    alarmSettingValidator,
    AlarmSettingValidationField,
} from 'ops/common/validators';
import { first, firstValueFrom, lastValueFrom, skip } from 'rxjs';

@Component({
    selector: 'app-custom-graph-dialog',
    templateUrl: './custom-graph-dialog.component.html',
    styleUrls: ['./custom-graph-dialog.component.scss'],
    standalone: false
})
export class CustomGraphDialogComponent {
    readonly GAUGE = CustomGraphType.gauge;
    readonly METER = CustomGraphType.meter;
    readonly INDICATOR = CustomGraphType.indicator;
    readonly CUSTOM_GRAPH_PAGE = PageTitle.CustomGraph;
    readonly MAX_DIVISIONS = gaugeMaxDivisions;
    readonly MIN_DIVISIONS = gaugeMinDivisions;

    @Input() showDialog: boolean = false;
    @Input() isFromRight!: boolean;
    @Input() selectedNum!: number;
    @Input() selectedCustomGraphItem: any = null;
    
    @Input() graphContainer: ElementRef;
    @Input() itemList: any[];
    @Input() selectedCoord: {
        x: number;
        y: number;
    } 

    @Output() onCloseDialog = new EventEmitter();
    @Output() onSetCustomGraph = new EventEmitter();

    @ViewChild('divisionInput') divisionInput: ElementRef;
    @ViewChild('customGraphDialog') customGraphDialog: ElementRef;
    @ViewChild('header') header: ElementRef;

    private isDragging = false;
    private offsetX = 0;
    private offsetY = 0;

    selectedType = '';
    selectedChannel = null;
    graphOptionList = [this.GAUGE, this.METER, this.INDICATOR];
    divisions: number;
    alarmSettingShow = false;

    isFixed: boolean = false;

    validators: { [key: string]: ObjectSchema<AlarmSetting> } = {
        veryLow: null,
        low: null,
        high: null,
        veryHigh: null,
    };

    constructor(
        private confirmationPromptService: ConfirmationPromptService,
        private translate: TranslateService,
        private channelService: ChannelService,
        private notificationService: NotificationService,
        private authService: AuthService
    ) {}

    ngOnInit(): void {
        this.divisions = this.selectedCustomGraphItem?.divisions
            ? this.selectedCustomGraphItem?.divisions
            : 6;
    }

    mouseDown(event) {
        if (this.isFixed || this.isDragging) return;
        this.isDragging = true;
        const draggableElement = this.customGraphDialog.nativeElement;

        if (event.touches) {
            this.offsetX = event.touches[0].clientX - draggableElement.offsetLeft;
            this.offsetY = event.touches[0].clientY - draggableElement.offsetTop;
        } else {
            this.offsetX = event.clientX - draggableElement.offsetLeft;
            this.offsetY = event.clientY - draggableElement.offsetTop;
        }

        (event.currentTarget as HTMLElement).style.cursor = 'grabbing';
    }

    @HostListener('document:mousemove', ['$event'])
    @HostListener('document:touchmove', ['$event'])
    moveDialog(event) {
        if (this.isDragging) {
            const draggableElement = this.customGraphDialog.nativeElement;
            if (event.touches) {
                draggableElement.style.top = `${event.touches[0].clientY - this.offsetY}px`;
                draggableElement.style.left = `${event.touches[0].clientX - this.offsetX}px`;
            } else {
                draggableElement.style.top = `${event.clientY - this.offsetY}px`;
                draggableElement.style.left = `${event.clientX - this.offsetX}px`;
            }
        }
    }

    @HostListener('document:mouseup')
    @HostListener('document:touchend')
    endMoveDialog() {
        if (this.isDragging) {
            this.isDragging = false;
            const draggableElement = this.header.nativeElement;
            draggableElement.style.cursor = 'grab';
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isFromRight) {
            if (this.customGraphDialog?.nativeElement) {
                const draggableElement = this.customGraphDialog.nativeElement;

                draggableElement.style.top = '201px';
                if (this.isFromRight) {
                    draggableElement.style.right = 'unset';
                    draggableElement.style.left = '208px';
                } else {
                    draggableElement.style.left = 'unset';
                    draggableElement.style.right = '208px';
                }
            }
            this.resetCheckedState();
        }

        if (changes.selectedCustomGraphItem) {
            if (this.selectedCustomGraphItem?.channel) {
                this.selectedChannel = this.selectedCustomGraphItem.channel;
                this.selectedType = this.selectedCustomGraphItem.type;
                this.divisions = this.selectedCustomGraphItem.divisions;
                this.alarmSettingShow = this.selectedCustomGraphItem.alarmSettingShow;
                this.initValidatorsChannel();
            } else {
                this.resetCheckedState();
            }
        } else if (changes.selectedNum) {
            this.resetCheckedState();
        }
    }

    changeSelectedGroupOption() {
        this.onSetCustomGraph.emit({
            type: this.selectedType,
            channel: this.selectedChannel,
            divisions: this.divisions,
            alarmSettingShow: this.selectedType !== this.INDICATOR ? false : this.alarmSettingShow,
        });
    }

    channelSelect(selectedChannel) {
        this.selectedChannel = selectedChannel;
        if (!this.selectedChannel.checked) this.resetCheckedState();
        this.initValidatorsChannel();
        if(
            this.selectedChannel &&
            (
                this.isDigital() ||
                this.isRunningHour()
            ) &&
            (
                this.selectedType === CustomGraphType.gauge || 
                this.selectedType === CustomGraphType.meter
            )
        ) {
            this.selectedType = '';
        }
        this.onSetCustomGraph.emit({ 
            type: this.selectedType,
            channel: this.selectedChannel,
            divisions: this.divisions,
            alarmSettingShow: this.alarmSettingShow,
        });
    }

    initValidatorsChannel() {
        let validators = {
            veryLow: null,
            low: null,
            high: null,
            veryHigh: null,
            feedback: null,
            sensorFail: null,
            alarmSetting: null,
        };
        validators.veryLow = alarmSettingValidator(
            this.selectedChannel,
            AlarmSettingValidationField.ALARM_VERY_LOW,
        );
        validators.low = alarmSettingValidator(
            this.selectedChannel,
            AlarmSettingValidationField.ALARM_LOW,
        );
        validators.high = alarmSettingValidator(
            this.selectedChannel,
            AlarmSettingValidationField.ALARM_HIGH,
        );
        validators.veryHigh = alarmSettingValidator(
            this.selectedChannel,
            AlarmSettingValidationField.ALARM_VERY_HIGH,
        );
        validators.feedback = alarmSettingValidator(
            this.selectedChannel,
            AlarmSettingValidationField.ALARM_SETTING_FEEDBACK_ERROR,
        );
        validators.sensorFail = alarmSettingValidator(
            this.selectedChannel,
            AlarmSettingValidationField.ALARM_SETTING_SENSOR_FAIL,
        );
        validators.alarmSetting = alarmSettingValidator(
            this.selectedChannel,
            AlarmSettingValidationField.ALARM_SETTING,
        );

        this.validators = Object.assign({}, validators);
    }

    minusDivisions() {
        this.divisions -= 1;
        this.divisionsChange();
    }

    plusDivisions() {
        this.divisions += 1;
        this.divisionsChange();
    }

    divisionsChange() {
        if (this.divisions > this.MAX_DIVISIONS) {
            this.divisionInput.nativeElement.value = this.MAX_DIVISIONS;
            this.divisions = this.MAX_DIVISIONS;
        } else if (this.divisions <= this.MIN_DIVISIONS) {
            this.divisionInput.nativeElement.value = this.MIN_DIVISIONS;
            this.divisions = this.MIN_DIVISIONS;
        }

        this.onSetCustomGraph.emit({
            type: this.selectedType,
            channel: this.selectedChannel,
            divisions: this.divisions,
            alarmSettingShow: this.alarmSettingShow,
        });
    }

    alarmSettingShowChange() {
        this.onSetCustomGraph.emit({
            type: this.selectedType,
            channel: this.selectedChannel,
            divisions: this.divisions,
            alarmSettingShow: this.alarmSettingShow,
        });
    }

    hasCheckedState(channel: any) {
        return channel?.checked !== undefined;
    }

    resetCheckedState() {
        if (this.hasCheckedState(this.selectedChannel)) this.selectedChannel.checked = false;
        this.selectedChannel = null;
        this.selectedType = '';
    }

    removeClick() {
        this.resetCheckedState();
        this.onSetCustomGraph.emit(null);
    }

    close() {
        this.onCloseDialog.emit();
    }

    isDigital() {
        return ChannelFns.isDigital(this.selectedChannel);
    }

    isRunningHour() {
        return ChannelFns.isRunningHour(this.selectedChannel);
    }

    emitSubmit(alarmSettingKey: string, alarmSetting: AlarmSetting) {
        this.createAlarmCallback(alarmSettingKey, this.onChannelUpdateClicked)(alarmSetting);
    }

    emitDelete(alarmSettingKey: string, alarmSetting: AlarmSetting) {
        this.confirmationPromptService.onOptionSelected().subscribe((confirm) => {
            if (confirm) {
                this.createAlarmCallback(alarmSettingKey, this.onAlarmDisableClicked)(alarmSetting);
            }
        });

        this.confirmationPromptService.showPrompt({
            mainLabel: this.translate.instant('forms.channel.alarm.delete-confirmation-prompt'),
            confirmLabel: this.translate.instant('forms.channel.alarm.delete-confirmation-button'),
            cancelLabel: this.translate.instant('forms.channel.alarm.delete-cancel-button'),
        });
    }

    onChannelUpdateClicked = (channel: Channel) => {
        this.updateChannel(channel).then(() => {
            this.notificationService.channelUpdateComplete();
        });
    };

    onAlarmDisableClicked = (channel: Channel) => {
        this.updateChannel(channel);
    };

    async updateChannel(channel: Channel) {
        try {
            await lastValueFrom(this.channelService.update(channel));
            const updatedChannel = await lastValueFrom(this.channelService.get(channel.id));

            this.selectedChannel.fcuData = updatedChannel.fcuData;
            this.selectedChannel.checked = true;
            this.initValidatorsChannel();
        } catch (e) {
            if (e.status == HttpStatusCode.Unauthorized) {
                this.authService.signOut();
                this.authService.showSignInOverlay();
                const loggedInUser = await firstValueFrom(
                    this.authService.currentUser$.pipe(skip(1), first()),
                );
                if (loggedInUser.token && loggedInUser.userId) {
                    this.updateChannel(channel);
                }
            }
        }
    }

    optionDisable(graphOption) {
        let result = false;
        
        if(!this.selectedChannel) return result;

        if(graphOption !== CustomGraphType.indicator) {
            result = this.isDigital() || this.isRunningHour();
            if(result) return result;
        }

        const parentRect = this.graphContainer.nativeElement.getBoundingClientRect();

        let rightWall = Math.ceil(parentRect.width);
        let bottomWall = Math.ceil(parentRect.height);

        let endX = Math.floor(this.selectedCoord.x);
        let endY = Math.floor(this.selectedCoord.y);

        endX += customGraphSize[graphOption].width;
        endY += customGraphSize[graphOption].height;

        let checkCoord = false;
        
        for (let customGraphItem of this.itemList) {
            let isOverlapping = (endX > rightWall) || (endY > bottomWall);
            if (isOverlapping) {
                checkCoord = true;
                break;
            }
            
            if (customGraphItem && (customGraphItem.index !== this.selectedNum)) {
                const startX = this.selectedCoord.x;
                const startY = this.selectedCoord.y;
            
                const isOverlapping =
                        (customGraphItem.x < endX && customGraphItem.endX > startX) &&
                        (customGraphItem.y < endY && customGraphItem.endY > startY) 

                if (isOverlapping) {
                    checkCoord = true;
                    break;
                }
            }
        }

        result = checkCoord;

        return result;
    }

    createAlarmCallback(alarmSettingKey: string, event: any) {
        return (alarmSetting: AlarmSetting) => {
            const newChannel = this.cloneChannel();
            newChannel.fcuData[alarmSettingKey] = alarmSetting;
            event(newChannel);
        };
    }

    cloneChannel(): Channel {
        return JSON.parse(JSON.stringify(this.selectedChannel));
    }

    fixDialog() {
        if (!this.customGraphDialog?.nativeElement) return;
        this.isFixed = !this.isFixed;
        const draggableElement = this.customGraphDialog.nativeElement;
        if (!this.isFixed) {
            draggableElement.style.top = '201px';
            if (this.isFromRight) {
                draggableElement.style.right = 'unset';
                draggableElement.style.left = '208px';
            } else {
                draggableElement.style.left = 'unset';
                draggableElement.style.right = '208px';
            }
        } else {
            draggableElement.style.top = '65px';
            if (this.isFromRight) {
                draggableElement.style.right = 'unset';
                draggableElement.style.left = '0px';
            } else {
                draggableElement.style.left = 'unset';
                draggableElement.style.right = '0px';
            }
        }
    }
}
